import React from 'react';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  LinkedIn as LinkedInIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { PotentialCandidate } from '../types';

interface PotentialCandidatesPrintViewProps {
  candidates: PotentialCandidate[];
  groupBy: 'none' | 'openingStatus' | 'location' | 'okToContact';
  groupedCandidates: Record<string, PotentialCandidate[]>;
}

const PotentialCandidatesPrintView: React.FC<PotentialCandidatesPrintViewProps> = ({
  candidates,
  groupBy,
  groupedCandidates,
}) => {
  // Light mode colors for print - using darker colors on light backgrounds
  const getStatusColor = (status: string | undefined) => {
    if (!status) return '#333';
    switch (status) {
      case '00-interested': return '#2e7d32'; // Dark green
      case '01-not-interested': return '#c62828'; // Dark red
      case '02-pending': return '#ef6c00'; // Dark orange
      case '03-contacted': return '#1565c0'; // Dark blue
      default: return '#333';
    }
  };

  const getStatusBackground = (status: string | undefined) => {
    if (!status) return '#f5f5f5';
    switch (status) {
      case '00-interested': return '#e8f5e9'; // Light green
      case '01-not-interested': return '#ffebee'; // Light red
      case '02-pending': return '#fff3e0'; // Light orange
      case '03-contacted': return '#e3f2fd'; // Light blue
      default: return '#f5f5f5';
    }
  };

  // Split candidates into pages (12 per page - 4 columns x 3 rows)
  const paginateCandidates = (candidateList: PotentialCandidate[]) => {
    const pages: PotentialCandidate[][] = [];
    for (let i = 0; i < candidateList.length; i += 12) {
      pages.push(candidateList.slice(i, i + 12));
    }
    return pages;
  };

  const renderCandidateCard = (candidate: PotentialCandidate) => (
    <div
      key={candidate.id}
      style={{
        border: '2px solid #333',
        borderRadius: '4px',
        padding: '8px',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        fontSize: '8px',
        pageBreakInside: 'avoid',
        minHeight: '180px',
        maxHeight: '220px',
        overflow: 'hidden',
        color: '#000',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px', gap: '6px' }}>
        <div style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: '#e0e0e0',
          border: '2px solid #666',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
        }}>
          {candidate.candidateImage?.[0]?.thumbnails?.small?.url ? (
            <img
              src={candidate.candidateImage[0].thumbnails.small.url}
              alt={candidate.candidateName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <PersonIcon style={{ fontSize: '14px', color: '#333' }} />
          )}
        </div>
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <div style={{ fontSize: '8px', fontWeight: 700, lineHeight: 1.2, marginBottom: '2px', color: '#000' }}>
            {candidate.candidateName}
          </div>
          {candidate.index && (
            <div style={{ fontSize: '6px', color: '#555' }}>
              #{candidate.index}
            </div>
          )}
        </div>
      </div>

      {/* Status Chips */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '3px', flexWrap: 'wrap' }}>
        {candidate.currentStatus && (
          <span
            style={{
              fontSize: '6px',
              padding: '2px 5px',
              borderRadius: '3px',
              backgroundColor: getStatusBackground(candidate.currentStatus),
              color: getStatusColor(candidate.currentStatus),
              border: `1px solid ${getStatusColor(candidate.currentStatus)}`,
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {candidate.currentStatus}
          </span>
        )}
        {candidate.level && (
          <span
            style={{
              fontSize: '6px',
              padding: '2px 5px',
              borderRadius: '3px',
              border: '1px solid #1565c0',
              backgroundColor: '#e3f2fd',
              color: '#1565c0',
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {candidate.level}
          </span>
        )}
        {candidate.openingStatus && (
          <span
            style={{
              fontSize: '6px',
              padding: '2px 5px',
              borderRadius: '3px',
              backgroundColor: '#e8f5e9',
              border: '1px solid #2e7d32',
              color: '#2e7d32',
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {candidate.openingStatus}
          </span>
        )}
      </div>

      {/* Job Info */}
      {(candidate.title || candidate.company) && (
        <div style={{ marginBottom: '3px', borderBottom: '1px solid #ccc', paddingBottom: '3px' }}>
          {candidate.title && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
              <WorkIcon style={{ fontSize: '7px', color: '#555' }} />
              <span style={{ fontSize: '6.5px', color: '#000', lineHeight: 1.2, fontWeight: 600 }}>
                {candidate.title}
              </span>
            </div>
          )}
          {candidate.company && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <BusinessIcon style={{ fontSize: '7px', color: '#555' }} />
              <span style={{ fontSize: '6.5px', color: '#000', lineHeight: 1.2, fontWeight: 600 }}>
                {candidate.company}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Contact Info */}
      <div style={{ marginBottom: '3px', flexGrow: 1 }}>
        {candidate.email && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
            <EmailIcon style={{ fontSize: '7px', color: '#555', flexShrink: 0 }} />
            <span
              style={{
                fontSize: '6px',
                color: '#000',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {candidate.email}
            </span>
          </div>
        )}
        {candidate.telNumber && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
            <PhoneIcon style={{ fontSize: '7px', color: '#555', flexShrink: 0 }} />
            <span style={{ fontSize: '6px', color: '#000' }}>
              {candidate.telNumber}
            </span>
          </div>
        )}
        {candidate.location && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
            <LocationIcon style={{ fontSize: '7px', color: '#555', flexShrink: 0 }} />
            <span style={{ fontSize: '6px', color: '#000' }}>
              {candidate.location}
            </span>
          </div>
        )}
        {candidate.linkedinProfile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
            <LinkedInIcon style={{ fontSize: '7px', color: '#0066b2', flexShrink: 0 }} />
            <span style={{ fontSize: '6px', color: '#0066b2', fontWeight: 600 }}>
              LinkedIn
            </span>
          </div>
        )}
      </div>

      {/* Notes */}
      {(candidate.notes || candidate.generalNotes) && (
        <div
          style={{
            fontSize: '5.5px',
            color: '#333',
            backgroundColor: '#f9f9f9',
            border: '1px solid #ddd',
            borderRadius: '2px',
            padding: '3px',
            marginBottom: '3px',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3,
          }}
        >
          {candidate.notes || candidate.generalNotes}
        </div>
      )}

      {/* Contact Status */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          borderTop: '1px solid #ccc',
          paddingTop: '3px',
          marginTop: 'auto',
        }}
      >
        {candidate.okToContact ? (
          <CheckCircleIcon style={{ fontSize: '8px', color: '#2e7d32' }} />
        ) : (
          <CancelIcon style={{ fontSize: '8px', color: '#c62828' }} />
        )}
        <span style={{ fontSize: '6px', color: '#000', fontWeight: 600 }}>
          {candidate.okToContact ? 'OK to Contact' : 'Do Not Contact'}
        </span>
      </div>
    </div>
  );

  return (
    <>
      <style>
        {`
          @page {
            size: landscape;
            margin: 0.5in;
          }

          /* Hide print view on screen */
          .print-view-root {
            display: none;
          }

          @media print {
            /* Force light mode for print */
            body {
              background-color: #ffffff !important;
              color: #000000 !important;
            }

            /* Hide the main screen content - THIS IS KEY */
            .screen-only-content {
              display: none !important;
            }

            /* Hide sidebar/drawer, top navigation bar, and footer */
            header,
            footer,
            .MuiAppBar-root,
            .MuiDrawer-root,
            nav {
              display: none !important;
            }

            /* Show ONLY the print view */
            .print-view-root {
              display: block !important;
              visibility: visible !important;
              background-color: #ffffff !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }

            .print-view-container {
              display: block !important;
              visibility: visible !important;
              background-color: #ffffff !important;
            }

            /* Ensure all nested elements in print view are visible */
            .print-view-root * {
              visibility: visible !important;
            }

            /* Force colors and backgrounds to print */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }

            /* Reset any Material-UI overrides */
            .print-view-root,
            .print-view-root *,
            .print-view-container,
            .print-view-container * {
              max-width: none !important;
              box-sizing: border-box !important;
            }
          }
        `}
      </style>

      <div className="print-view-root">
        <div className="print-view-container">
        {Object.entries(groupedCandidates).map(([groupName, groupCandidates], groupIndex) => {
          const pages = paginateCandidates(groupCandidates);

          return pages.map((pageCandidates, pageIndex) => (
            <div
              key={`${groupName}-${pageIndex}`}
              style={{
                pageBreakAfter: pageIndex < pages.length - 1 || groupIndex < Object.keys(groupedCandidates).length - 1 ? 'always' : 'auto',
                pageBreakInside: 'avoid',
                width: '100%',
                padding: '10px',
                boxSizing: 'border-box',
              }}
            >
              {/* Page Header */}
              <div
                style={{
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '3px solid #000',
                  backgroundColor: '#f5f5f5',
                  padding: '10px',
                  borderRadius: '4px',
                }}
              >
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#000',
                    marginBottom: '4px',
                  }}
                >
                  Potential Candidates Report
                  {groupBy !== 'none' && ` - ${groupName}`}
                </div>
                <div style={{ fontSize: '9px', color: '#333', fontWeight: 500 }}>
                  Page {pageIndex + 1} of {pages.length}
                  {groupBy !== 'none' && ` | Group ${groupIndex + 1} of ${Object.keys(groupedCandidates).length}`}
                  {' | '}
                  {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
                  {' | '}
                  Total Candidates: {groupCandidates.length}
                </div>
              </div>

              {/* Grid of 12 candidates (4 columns x 3 rows) */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gridAutoRows: '1fr',
                  gap: '8px',
                  width: '100%',
                  height: 'auto',
                }}
              >
                {pageCandidates.map((candidate) => renderCandidateCard(candidate))}
                {/* Fill empty spaces if less than 12 candidates */}
                {Array.from({ length: 12 - pageCandidates.length }).map((_, idx) => (
                  <div key={`empty-${idx}`} style={{
                    border: '2px dashed #bbb',
                    borderRadius: '4px',
                    backgroundColor: '#fafafa'
                  }} />
                ))}
              </div>
            </div>
          ));
        })}
        </div>
      </div>
    </>
  );
};

export default PotentialCandidatesPrintView;
